import { useEffect, useMemo, useState } from 'react';
import { shell } from '../../shell/shellClasses';
import { overlayCalcCsv } from '../debug-export/blockCsv';
import { downloadCsv } from '../debug-export/csv';
import { defaultEnabledIds, exportOverlayCsv } from './buildSeries';
import { MetricToggleBar } from './MetricToggleBar';
import { OverlayChart } from './OverlayChart';
import type { MasterOverlayModel, OverlayMetricId } from './types';

export function MasterOverlayPanel({
  model,
  loading,
  error,
  onRetry,
  compareModeLabel,
}: {
  model: MasterOverlayModel | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  compareModeLabel: string;
}) {
  const [enabled, setEnabled] = useState<Set<OverlayMetricId>>(
    () => new Set(defaultEnabledIds()),
  );

  useEffect(() => {
    setEnabled(new Set(defaultEnabledIds()));
  }, [model?.connectionId, model?.from, model?.to]);

  const enabledList = useMemo(() => [...enabled], [enabled]);
  const compareOn = Boolean(model?.priorLabel);

  const onToggle = (id: OverlayMetricId) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size <= 1) return prev;
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const onExport = () => {
    if (!model) return;
    downloadCsv(`founder-overlay-${model.from}_${model.to}.csv`, exportOverlayCsv(model, enabledList, compareOn));
    downloadCsv(`dashboard_overlay_${model.from}_${model.to}.csv`, overlayCalcCsv(model));
  };

  return (
    <section
      id="widget-overlay-graph"
      data-widget-id="overlay-graph"
      aria-labelledby="master-overlay-heading"
      className="space-y-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2
          id="master-overlay-heading"
          className="text-sm font-semibold tracking-tight text-[var(--shell-fg)]"
        >
          Business performance trend
          {model ? (
            <span className={`ml-2 font-normal ${shell.muted}`}>
              {model.periodLabel}
              {model.priorLabel ? ` vs ${compareModeLabel}` : ''}
            </span>
          ) : null}
        </h2>
        <button
          type="button"
          className={`${shell.control} ${shell.focusRing} border-transparent bg-transparent ${shell.muted} ${shell.hoverRow}`}
          onClick={onExport}
          disabled={!model || loading}
        >
          Export CSV
        </button>
      </div>

      {model && (
        <MetricToggleBar metrics={model.metrics} enabled={enabled} onToggle={onToggle} />
      )}

      {error && (
        <div
          role="alert"
          className="rounded-[var(--shell-radius-md)] bg-[var(--shell-danger)]/10 px-3 py-2 text-sm text-[var(--shell-danger)]"
        >
          {error}{' '}
          <button type="button" className="underline" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      <div className={`rounded-[var(--shell-radius-lg)] ${shell.surfaceElevated} p-3 sm:p-4`}>
        {loading && !model ? (
          <div
            className="h-[280px] animate-pulse rounded-[var(--shell-radius-md)] bg-[var(--shell-hover)] motion-reduce:animate-none"
            aria-busy
            aria-label="Loading overlay chart"
          />
        ) : model ? (
          <>
            <OverlayChart
              model={model}
              enabled={enabledList}
              compare={compareOn}
              connectionId={model.connectionId}
            />
            <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-[var(--shell-fg-muted)]">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-0.5 w-5 bg-[var(--shell-fg)]" aria-hidden /> Current period
              </span>
              {compareOn && (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="h-0.5 w-5 border-t border-dashed border-[var(--shell-fg-muted)]"
                    aria-hidden
                  />{' '}
                  Previous ({model.priorLabel})
                </span>
              )}
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
